import React from 'react'

// @ts-expect-error - untyped bundle, same suppression as MediaGridReport.tsx.
import { Carousel, Panzoom } from '@fancyapps/ui'

import '@fancyapps/ui/dist/carousel.css'
import '@fancyapps/ui/dist/panzoom.css'

//@ts-ignore
import styles from './ProductPhotosTab.module.css'

export interface ProductPhotosTabProps {
  alt: string
  /** Swipe left past the last photo: next product (Monday 13246279222). */
  onSwipeNext?: () => void
  /** Swipe right past the first photo: previous product. */
  onSwipePrev?: () => void
  photos: readonly string[]
}

interface PanzoomLike {
  content: { height: number; width: number }
  state: string
  transform: { scale: number }
  viewport: { height: number; width: number }
}

// Same test Panzoom runs for panOnlyZoomed: at fit size a one-finger drag is
// not a pan.
const isZoomed = (zoom: PanzoomLike): boolean =>
  !(
    zoom.content.width <= zoom.viewport.width &&
    zoom.content.height <= zoom.viewport.height &&
    zoom.transform.scale <= 1
  )

const SWIPE_MIN_DX = 60
const SWIPE_MAX_MS = 700

const escapeAttr = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')

// Swipe between photos; pinch / double-tap / wheel zoom on each one. Panzoom
// runs with panOnlyZoomed so a one-finger swipe on an unzoomed photo still
// reaches the carousel (same arrangement Fancybox uses for its own slides).
// Images keep object-fit: contain, so NVR / S4 photos are never cropped
// (Monday 13110542552).
export default function ProductPhotosTab({
  alt,
  onSwipeNext,
  onSwipePrev,
  photos,
}: ProductPhotosTabProps) {
  const hostRef = React.useRef<HTMLDivElement>(null)
  const rootRef = React.useRef<HTMLDivElement>(null)
  const [index, setIndex] = React.useState(0)
  const swipeRef = React.useRef({ next: onSwipeNext, prev: onSwipePrev })
  swipeRef.current = { next: onSwipeNext, prev: onSwipePrev }

  React.useEffect(() => {
    const host = hostRef.current
    if (!host) return
    // One Panzoom per slide element. Registered through the `on` option
    // because the constructor already creates the slides: a handler added
    // afterwards would never see them.
    const zooms = new Map<HTMLElement, PanzoomLike & { destroy: () => void }>()
    // Panzoom's default `click: "toggleZoom"` zooms to native size on a
    // single tap. On a phone that is the first thing a customer does, and the
    // 1200px photo ends up cut off past the right and bottom edges. On touch
    // screens only a double tap toggles the zoom (pinch keeps working).
    const touchScreen = window.matchMedia(
      '(hover: none) and (pointer: coarse)',
    ).matches
    let lastTap = 0
    const onZoomClick = (): boolean => {
      if (!touchScreen) return true
      const now = Date.now()
      const isDoubleTap = now - lastTap < 350
      lastTap = isDoubleTap ? 0 : now
      return isDoubleTap
    }
    const carousel = new Carousel(host, {
      Dots: false,
      infinite: false,
      on: {
        change: (self: { page: number | null }) => setIndex(self.page ?? 0),
        createSlide: (_: unknown, slide: { $el: HTMLElement }) => {
          const wrap = slide.$el.querySelector<HTMLElement>(
            `.${styles['zoom']}`,
          )
          const image = wrap?.querySelector('img')
          if (!wrap || !image) return
          zooms.set(
            slide.$el,
            new Panzoom(wrap, {
              content: image,
              // Panzoom swaps a plain number for naturalWidth / fitWidth on
              // images (updateMetrics), i.e. zoom up to native resolution,
              // same as Fancybox. Kept that way: no upscaled blur.
              maxScale: 4,
              // Keep .zoom at the slide size: with the default, Panzoom pins
              // px sizes on it and its resize observer never sees a resize.
              // .zoom is then the pan viewport and the img sits at its
              // top-left: Panzoom centers it and bounds the pan itself.
              resizeParent: false,
              panOnlyZoomed: true,
              wrapInner: false,
              // Returning false skips the toggle (Panzoom.js onClick).
              on: { click: onZoomClick },
            }),
          )
        },
        removeSlide: (_: unknown, slide: { $el: HTMLElement | null }) => {
          if (!slide.$el) return
          zooms.get(slide.$el)?.destroy()
          zooms.delete(slide.$el)
        },
      },
      preload: photos.length,
      slides: photos.map((src) => ({
        html: `<div class="${styles['zoom']}"><img alt="${escapeAttr(
          alt,
        )}" src="${escapeAttr(src)}" draggable="false"></div>`,
      })),
    })

    // Product to product (13246279222): a quick one-finger horizontal swipe
    // on an unzoomed photo that the carousel cannot follow (first / last
    // photo). Products with several photos go through their photos first.
    // Capture phase and passive: Panzoom and the carousel still get every
    // event; this only watches. Two fingers (pinch), a zoomed photo or a
    // running animation cancel it. Mouse drags never reach here (touch only).
    const root = rootRef.current
    let swipe: {
      canNext: boolean
      canPrev: boolean
      t: number
      x: number
      y: number
    } | null = null
    const onTouchStart = (event: TouchEvent): void => {
      const touch = event.touches[0]
      if (event.touches.length !== 1 || !touch) {
        swipe = null
        return
      }
      const pages = (carousel.pages as unknown[]).length
      const page: number = carousel.page ?? 0
      const slideEl: HTMLElement | undefined = carousel.slides[page]?.$el
      const zoom = slideEl ? zooms.get(slideEl) : undefined
      if (
        (zoom && (isZoomed(zoom) || zoom.state !== 'ready')) ||
        carousel.Panzoom?.state !== 'ready'
      ) {
        swipe = null
        return
      }
      swipe = {
        canNext: page >= pages - 1,
        canPrev: page <= 0,
        t: Date.now(),
        x: touch.clientX,
        y: touch.clientY,
      }
    }
    const onTouchMove = (event: TouchEvent): void => {
      if (event.touches.length > 1) swipe = null
    }
    const onTouchEnd = (event: TouchEvent): void => {
      const start = swipe
      const touch = event.changedTouches[0]
      if (event.touches.length > 0) swipe = null
      if (!start || !touch || event.touches.length > 0) return
      swipe = null
      const dx = touch.clientX - start.x
      const dy = touch.clientY - start.y
      if (
        Math.abs(dx) < SWIPE_MIN_DX ||
        Math.abs(dx) <= Math.abs(dy) ||
        Date.now() - start.t > SWIPE_MAX_MS
      ) {
        return
      }
      const go =
        dx < 0
          ? start.canNext && swipeRef.current.next
          : start.canPrev && swipeRef.current.prev
      if (go) go()
    }
    const onTouchCancel = (): void => {
      swipe = null
    }
    const capture = { capture: true, passive: true }
    root?.addEventListener('touchstart', onTouchStart, capture)
    root?.addEventListener('touchmove', onTouchMove, capture)
    root?.addEventListener('touchend', onTouchEnd, capture)
    root?.addEventListener('touchcancel', onTouchCancel, capture)

    return () => {
      root?.removeEventListener('touchstart', onTouchStart, capture)
      root?.removeEventListener('touchmove', onTouchMove, capture)
      root?.removeEventListener('touchend', onTouchEnd, capture)
      root?.removeEventListener('touchcancel', onTouchCancel, capture)
      carousel.destroy()
      zooms.forEach((zoom) => zoom.destroy())
      zooms.clear()
      host.replaceChildren()
    }
  }, [alt, photos])

  return (
    <div className={styles['root']} ref={rootRef}>
      <div className={`carousel ${styles['carousel']}`} ref={hostRef} />
      {photos.length > 1 && (
        <p aria-live="polite" className={styles['counter']}>
          {index + 1} / {photos.length}
        </p>
      )}
    </div>
  )
}
